import React, {useState, useEffect} from 'react';
import './AuditLog.css';
import {getAuditLogs, subscribeToAuthReady} from '../services/apiService';
import Button from './ui/Button';
import FilterToggleButton from './ui/FilterToggleButton';

const AuditLog = () => {
    const [auditLogData, setAuditLogData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        vonDatum: '', bisDatum: '', aktion: '', nutzer: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                setLoading(true);
                const data = await getAuditLogs();
                setAuditLogData(data || []);
                setError(null);
            } catch (err) {
                // 401 Fehler beim ersten Laden ignorieren (passiert, wenn Token noch nicht bereit)
                if (err.status === 401) {
                    console.log('Verzögere Laden des Audit Logs (noch nicht authentifiziert)');
                    // Wir warten aktiv darauf, dass das Token bereit ist
                    subscribeToAuthReady(() => {
                        console.log('Token jetzt bereit, lade Audit Log erneut...');
                        fetchLogs();
                    });
                    setLoading(false);
                    return;
                }
                console.error('Fehler beim Laden der Audit Logs:', err);
                const msg = err.message || 'Audit Logs konnten nicht geladen werden.';
                setError(msg);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, []);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({...prev, [field]: value}));
        setCurrentPage(1);
    };

    const handleReset = () => {
        setFilters({
            vonDatum: '', bisDatum: '', aktion: '', nutzer: ''
        });
        setCurrentPage(1);
    };

    const formatDate = (dateArrayOrString) => {
        if (!dateArrayOrString) return {date: '', time: ''};
        // Backend liefert LocalDateTime, was als ISO-String oder Array ankommen kann
        const d = new Date(dateArrayOrString);
        if (isNaN(d.getTime())) return {date: String(dateArrayOrString), time: ''};

        const date = d.toLocaleDateString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });

        const time = d.toLocaleTimeString('de-DE', {
            hour: '2-digit', minute: '2-digit'
        });

        return {date, time};
    }

    // Parse Details-String und extrahiere Service, Method und Object
    const parseDetails = (detailsString) => {
        if (!detailsString) return null;

        const details = [];

        // Extrahiere Service
        const serviceMatch = detailsString.match(/Service:\s*([^,]+)/);
        if (serviceMatch) {
            details.push(`Service: ${serviceMatch[1].trim()}`);
        }

        // Extrahiere Method
        const methodMatch = detailsString.match(/Method:\s*([^,]+)/);
        if (methodMatch) {
            details.push(`Method: ${methodMatch[1].trim()}`);
        }

        // Extrahiere Objekt (falls vorhanden)
        const objectMatch = detailsString.match(/Objekt:\s*(.+?)(?:,|$)/);
        if (objectMatch) {
            details.push(`Objekt: ${objectMatch[1].trim()}`);
        } else {
            // Alternativ: Result size oder andere Informationen
            const resultMatch = detailsString.match(/Result size:\s*(\d+)/);
            if (resultMatch) {
                details.push(`Result size: ${resultMatch[1]}`);
            }
        }

        return details.length > 0 ? details : null;
    }

    // Filtere Daten basierend auf Eingaben (min. 2 Zeichen für Textfelder)
    const filteredData = auditLogData.filter(entry => {

        const nutzerMatch = filters.nutzer.length < 2 || (entry.username && entry.username.toLowerCase().includes(filters.nutzer.toLowerCase()));

        const aktionMatch = filters.aktion.length < 2 || (entry.action && entry.action.toLowerCase().includes(filters.aktion.toLowerCase()));

        let dateMatch = true;
        if (filters.vonDatum || filters.bisDatum) {
            const entryDate = new Date(entry.timestamp);
            if (filters.vonDatum) {
                const fromDate = new Date(filters.vonDatum);
                fromDate.setHours(0, 0, 0, 0);
                dateMatch = dateMatch && entryDate >= fromDate;
            }
            if (filters.bisDatum) {
                const toDate = new Date(filters.bisDatum);
                toDate.setHours(23, 59, 59, 999);
                dateMatch = dateMatch && entryDate <= toDate;
            }
        }

        return nutzerMatch && aktionMatch && dateMatch;
    });

    // Pagination
    const totalPages = Math.ceil(filteredData.length / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + entriesPerPage);

    // Generiere Seitenzahlen (max 4 zwischen Vor/Zurück)
    const getPageNumbers = () => {
        const pages = [];
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, start + 3);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (<div className="page-container">
            <h2 className="page-title">Audit Log</h2>

            {!loading && error && (
                <div className="submit-status error" role="alert">{error}</div>
            )}

            {/* Filter Row */}
            <div className="top-block">
                <div className="filter-header">
                    <FilterToggleButton
                        isExpanded={isFilterExpanded}
                        onToggle={() => setIsFilterExpanded(!isFilterExpanded)}
                    />
                </div>

                {isFilterExpanded && (
                    <div className="filter-content">
                        <div className="top-block-filter-row">
                            <div className="form-field">
                                <label htmlFor="audit-von-datum">Von</label>
                                <input
                                    id="audit-von-datum"
                                    type="date"
                                    value={filters.vonDatum}
                                    onChange={(e) => handleFilterChange('vonDatum', e.target.value)}
                                />
                            </div>
                            <div className="form-field">
                                <label htmlFor="audit-bis-datum">Bis</label>
                                <input
                                    id="audit-bis-datum"
                                    type="date"
                                    value={filters.bisDatum}
                                    onChange={(e) => handleFilterChange('bisDatum', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="top-block-filter-row">
                            <div className="form-field">
                                <label htmlFor="audit-aktion">Aktion</label>
                                <input
                                    id="audit-aktion"
                                    type="text"
                                    value={filters.aktion}
                                    onChange={(e) => handleFilterChange('aktion', e.target.value)}
                                    placeholder="Min. 2 Zeichen eingeben"
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="audit-nutzer">Nutzer</label>
                                <input
                                    id="audit-nutzer"
                                    type="text"
                                    value={filters.nutzer}
                                    onChange={(e) => handleFilterChange('nutzer', e.target.value)}
                                    placeholder="Min. 2 Zeichen eingeben"
                                />
                            </div>
                        </div>
                        <div className="top-block-reset-btn">
                            <div className="form-field">
                                <label>&nbsp;</label>
                                <Button variant="outline-primary" icon ariaLabel="Filter zurücksetzen"
                                        title="Filter zurücksetzen" onClick={handleReset}>↺</Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Tabelle */}
            <div className="middle-block">
                <div className="common-section">
                    <table className="common-table">
                        <thead>
                        <tr>
                            <th>Zeitpunkt</th>
                            <th>Aktion</th>
                            <th>Details</th>
                            <th>Nutzer</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (<tr>
                                <td colSpan="4" className="no-data">Lade Audit Logs...</td>
                            </tr>) : error ? (<tr>
                                <td colSpan="4" className="no-data error">Einträge konnten nicht geladen werden.</td>
                            </tr>) : paginatedData.length === 0 ? (<tr>
                                <td colSpan="4" className="no-data">Keine Einträge gefunden</td>
                            </tr>) : (paginatedData.map((entry, idx) => {
                                const parsedDetails = parseDetails(entry.details);
                                const {date, time} = formatDate(entry.timestamp);
                                return (<tr key={entry.id} className={`common-row${idx % 2 === 1 ? ' zebra' : ''}`}>
                                        <td>
                                            <div className="timestamp-container">
                                                <div>{date}</div>
                                                <div>{time}</div>
                                            </div>
                                        </td>
                                        <td>{entry.action}</td>
                                        <td>
                                            {parsedDetails ? (<ul className="audit-details-list">
                                                    {parsedDetails.map((detail, i) => (<li key={i}>{detail}</li>))}
                                                </ul>) : (entry.details || '-')}
                                        </td>
                                        <td>{entry.username}</td>
                                    </tr>);
                            }))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="bottom-block">
                <div className="bottom-block-right">
                    <Button
                        variant="outline-primary"
                        icon
                        disabled={currentPage === 1}
                        ariaLabel="Vorherige Seite"
                        title="Vorherige Seite"
                        tooltip="Vorherige Seite"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    >
                        ‹
                    </Button>
                    {getPageNumbers().map(pageNum => (<Button
                            key={pageNum}
                            variant={currentPage === pageNum ? 'primary' : 'outline-primary'}
                            size="sm"
                            ariaLabel={`Seite ${pageNum} öffnen`}
                            title={`Seite ${pageNum}`}
                            tooltip={currentPage === pageNum ? `Aktive Seite ${pageNum}` : `Seite ${pageNum} öffnen`}
                            onClick={() => setCurrentPage(pageNum)}
                        >
                            {pageNum}
                        </Button>))}
                    <Button
                        variant="outline-primary"
                        icon
                        disabled={currentPage === totalPages || totalPages === 0}
                        ariaLabel="Nächste Seite"
                        title="Nächste Seite"
                        tooltip="Nächste Seite"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    >
                        ›
                    </Button>
                </div>

                <div className="bottom-block-right entries-per-page">
                    <select
                        value={entriesPerPage}
                        onChange={(e) => {
                            setEntriesPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10 Einträge</option>
                        <option value={20}>20 Einträge</option>
                        <option value={50}>50 Einträge</option>
                        <option value={100}>100 Einträge</option>
                    </select>
                </div>
            </div>
        </div>);
};

export default AuditLog;
